'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  ListMusic,
  Music,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Youtube,
  Trash2,
  Loader2,
  CalendarDays,
  Home,
  ShieldCheck,
  AlertCircle,
  Wallet,
  HelpCircle,
} from 'lucide-react';
import { CreditBadge } from '@/components/billing/CreditBadge';
import { trackEvent } from '@/lib/analytics/track';
import { AXIOS_INSTANCE } from '@/lib/api/orval/custom-instance';
import { Z_INDEX } from '@/lib/constants/zIndex';
import { useAuth } from '@/lib/hooks';
import { useChannelContext, makeChannelHref } from '@/lib/hooks/useChannelContext';
import { useToast } from '@/lib/hooks/useToast';
import { useOnboardingStore } from '@/lib/stores/onboardingStore';
import { useSidebarStore } from '@/lib/stores/uiStore';
import { cn } from '@/lib/utils';
import { SidebarChannelSelector } from './SidebarChannelSelector';

interface MenuItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  testId?: string;
  /** true면 user.isStaff인 사용자에게만 노출 */
  adminOnly?: boolean;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
  showHeader: boolean;
}

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const pathname = usePathname();
  const { isExpanded, toggleExpanded } = useSidebarStore();
  const { user, logout } = useAuth();
  const isStaff = user?.isStaff ?? false;
  const { success, error: toastError } = useToast();
  const queryClient = useQueryClient();
  const startOnboarding = useOnboardingStore((s) => s.startOnboarding);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const { currentChannelId, isMultiChannel } = useChannelContext();

  const handleClearCache = async () => {
    if (isClearingCache) { return; }
    setIsClearingCache(true);
    try {
      await AXIOS_INSTANCE.post('/api/system/cache/clear/');
      queryClient.clear();
      success('캐시가 초기화되었습니다.');
    } catch {
      toastError('캐시 초기화에 실패했습니다.');
    } finally {
      setIsClearingCache(false);
    }
  };

  // 쿼리 파라미터 제외한 pathname만 비교
  const isActive = (href: string) => {
    const cleanHref = href.split('?')[0];
    if (cleanHref === '/dashboard') {
      return pathname === '/dashboard' || pathname === '/';
    }
    return pathname === cleanHref || pathname?.startsWith(cleanHref + '/');
  };

  const getUserInitial = () => {
    if (user?.email) {return user.email[0].toUpperCase();}
    return 'U';
  };

  // ── 메뉴 그룹 빌드 ──────────────────────────────────────────────────────
  // 채널 컨텍스트 그룹: currentChannelId가 있을 때만 의미 있음
  const channelItems: MenuItem[] = currentChannelId
    ? [
        {
          href: `/channels/${currentChannelId}`,
          label: '채널 홈',
          icon: <Home className="w-5 h-5" />,
          testId: 'nav-channel-home',
        },
        {
          href: makeChannelHref('/playlists', currentChannelId),
          label: '플레이리스트',
          icon: <ListMusic className="w-5 h-5" />,
          testId: 'nav-playlists',
        },
        {
          href: makeChannelHref('/playlists/queue', currentChannelId),
          label: '위클리 큐',
          icon: <CalendarDays className="w-5 h-5" />,
          testId: 'nav-weekly-queue',
        },
      ]
    : [
        {
          href: '/playlists',
          label: '플레이리스트',
          icon: <ListMusic className="w-5 h-5" />,
          testId: 'nav-playlists',
        },
        {
          href: '/playlists/queue',
          label: '위클리 큐',
          icon: <CalendarDays className="w-5 h-5" />,
          testId: 'nav-weekly-queue',
        },
      ];

  // 전역 그룹 — 운영 도구(트랙/AI Agent/비디오 스튜디오/파일/크레딧 충전/API 문서)는
  // 모두 /admin 페이지로 이동했음. 관리자에게도 일반 사용자와 동일한 사이드바를
  // 보이게 하고, /admin 진입만 별도 노출.
  const globalItems: MenuItem[] = [
    {
      href: '/admin',
      label: '관리자',
      icon: <ShieldCheck className="w-5 h-5" />,
      testId: 'nav-admin',
      adminOnly: true,
    },
    {
      href: '/errors',
      label: '에러 인박스',
      icon: <AlertCircle className="w-5 h-5" />,
      testId: 'nav-errors',
      adminOnly: true,
    },
    {
      href: '/dashboard',
      label: '전체 대시보드',
      icon: <LayoutDashboard className="w-5 h-5" />,
      testId: 'nav-dashboard',
    },
    {
      href: '/channels',
      label: '채널 관리',
      icon: <Youtube className="w-5 h-5" />,
      testId: 'nav-channels',
    },
    {
      href: '/settings/billing',
      label: '크레딧 & 결제',
      icon: <Wallet className="w-5 h-5" />,
      testId: 'nav-billing',
    },
  ];

  // adminOnly 항목은 isStaff인 사용자에게만 노출
  const filterByRole = (items: MenuItem[]) =>
    items.filter((item) => !item.adminOnly || isStaff);

  // 다채널: 그룹 헤더 표시 / 1채널: 평평한 메뉴 (헤더 없음)
  const MENU_GROUPS: MenuGroup[] = [
    {
      title: '채널',
      items: filterByRole(channelItems),
      showHeader: isMultiChannel,
    },
    {
      title: '전역',
      items: filterByRole(globalItems),
      showHeader: isMultiChannel,
    },
  ];

  return (
    <aside
      className={cn(
        'fixed top-0 left-0 h-screen bg-white border-r border-neutral-200',
        'flex flex-col transition-all duration-300 ease-in-out',
        isExpanded ? 'w-60' : 'w-16',
        className
      )}
      style={{ zIndex: Z_INDEX.sidebar }}
      role="navigation"
      aria-label="메인 메뉴"
    >
      {/* 로고 영역 */}
      <div
        className={cn(
          'flex items-center h-16 px-4 border-b border-neutral-200',
          isExpanded ? 'justify-between' : 'justify-center'
        )}
      >
        <Link
          href="/dashboard"
          className={cn(
            'flex items-center gap-2 text-purple-600 hover:text-purple-700 transition',
            !isExpanded && 'justify-center'
          )}
        >
          <Music className="w-6 h-6 flex-shrink-0" />
          {isExpanded && (
            <span className="font-bold text-lg text-neutral-900 whitespace-nowrap">
              Auto-Playlist
            </span>
          )}
        </Link>

        {isExpanded && (
          <button
            onClick={toggleExpanded}
            className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
            aria-label="사이드바 접기"
            aria-expanded={isExpanded}
          >
            <ChevronLeft className="w-5 h-5 text-neutral-500" />
          </button>
        )}
      </div>

      {!isExpanded && (
        <button
          onClick={toggleExpanded}
          className="flex items-center justify-center h-10 mx-2 my-2 rounded-lg hover:bg-neutral-100 transition-colors"
          aria-label="사이드바 펼치기"
          aria-expanded={isExpanded}
        >
          <ChevronRight className="w-5 h-5 text-neutral-500" />
        </button>
      )}

      {/* 채널 셀렉터 */}
      <div className={cn('py-2 border-b border-neutral-100', !isExpanded && 'py-1')}>
        <SidebarChannelSelector isExpanded={isExpanded} />
      </div>

      {/* 메뉴 그룹 */}
      <nav className="flex-1 overflow-y-auto py-4">
        {MENU_GROUPS.map((group) => (
          <div key={group.title} className="mb-4">
            {isExpanded && group.showHeader && (
              <h3 className="px-4 mb-2 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                {group.title}
              </h3>
            )}

            <ul className="space-y-1 px-2">
              {group.items.map((item) => (
                <li key={item.testId ?? item.href}>
                  <Link
                    href={item.href}
                    data-testid={item.testId}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                      isActive(item.href)
                        ? 'bg-purple-50 text-purple-600'
                        : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900',
                      !isExpanded && 'justify-center px-0'
                    )}
                    title={!isExpanded ? item.label : undefined}
                  >
                    <span
                      className={cn(
                        'flex-shrink-0',
                        isActive(item.href) ? 'text-purple-600' : 'text-neutral-500'
                      )}
                    >
                      {item.icon}
                    </span>
                    {isExpanded && (
                      <span className="text-sm font-medium">{item.label}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* 크레딧 잔액 + 버전 */}
      <div
        className={cn(
          'px-4 py-2 border-t border-neutral-100 flex items-center',
          !isExpanded ? 'justify-center flex-col gap-1' : 'justify-between'
        )}
      >
        <CreditBadge compact={!isExpanded} />
        <span
          className={cn(
            'text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded font-mono',
            !isExpanded && 'text-[10px] px-1'
          )}
        >
          v{process.env.NEXT_PUBLIC_APP_VERSION || 'dev'}
        </span>
      </div>

      {/* 하단 고정: 사용자 프로필 + 투어 + 캐시 초기화 + 로그아웃 */}
      <div className="border-t border-neutral-200 p-2 space-y-1">
        {/* 사용자 프로필 — 로그아웃과 같은 그룹에 배치하여 "이 계정에서 로그아웃" 의미를 강화 */}
        <div
          className={cn(
            'flex items-center gap-3 px-3 py-2',
            !isExpanded && 'justify-center px-0'
          )}
        >
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <span className="text-sm font-medium text-purple-600">{getUserInitial()}</span>
          </div>
          {isExpanded && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {user?.email || '사용자'}
              </p>
            </div>
          )}
        </div>
        <button
          onClick={() => {
            trackEvent('onboarding_restart_clicked', { source: 'sidebar' });
            startOnboarding();
          }}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg',
            'text-neutral-600 hover:bg-purple-50 hover:text-purple-600 transition-all duration-200',
            !isExpanded && 'justify-center px-0'
          )}
          title={!isExpanded ? '투어 다시 보기' : undefined}
        >
          <HelpCircle className="w-5 h-5 flex-shrink-0" />
          {isExpanded && <span className="text-sm font-medium">투어 다시 보기</span>}
        </button>
        <button
          onClick={handleClearCache}
          disabled={isClearingCache}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg',
            'text-neutral-600 hover:bg-amber-50 hover:text-amber-600 transition-all duration-200',
            isClearingCache && 'opacity-50 cursor-not-allowed',
            !isExpanded && 'justify-center px-0'
          )}
          title={!isExpanded ? '캐시 초기화' : undefined}
        >
          {isClearingCache ? (
            <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin" />
          ) : (
            <Trash2 className="w-5 h-5 flex-shrink-0" />
          )}
          {isExpanded && (
            <span className="text-sm font-medium">
              {isClearingCache ? '초기화 중...' : '캐시 초기화'}
            </span>
          )}
        </button>
        <button
          onClick={() => logout()}
          data-testid="logout-btn"
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg',
            'text-neutral-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200',
            !isExpanded && 'justify-center px-0'
          )}
          title={!isExpanded ? '로그아웃' : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {isExpanded && <span className="text-sm font-medium">로그아웃</span>}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
